import { useQuery } from "@tanstack/react-query";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import { getInsightsAwardedRedeemed } from "../../actions/acc_actions";
import Loading from "../../utils/Loading";

// Register Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
);

const RewardsChart = ({ dateFrom, dateTo, selectedSchema }) => {
  const { isLoading, data: AwardedRedeemedData } = useQuery({
    queryKey: ["insightsAwardedRedeemed", selectedSchema, dateFrom, dateTo],
    queryFn: () =>
      getInsightsAwardedRedeemed({
        to: dateTo,
        from: dateFrom,
        refresh: false,
        rewardType: "all",
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: !!dateTo && !!dateFrom && !!selectedSchema,
    keepPreviousData: true,
  });

  const chartData = AwardedRedeemedData?.body?.data;

  // Show loading state
  if (isLoading) {
    return <Loading />;
  }

  // Return empty div if chartData is undefined
  if (chartData === undefined) {
    return <div />;
  }

  // Prepare data for chart
  const labels = chartData.map((item) => item.date);
  const vouchersAwardedData = chartData.map((item) => item.vouchers_awarded);
  const rewardsRedeemedData = chartData.map((item) => item.rewards_redeemed);

  // Chart configuration
  const chartConfig = {
    labels,
    datasets: [
      {
        label: "Vouchers Awarded",
        data: vouchersAwardedData,
        borderColor: "#1890ff",
        backgroundColor: "rgba(24, 144, 255, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Rewards Redeemed",
        data: rewardsRedeemedData,
        borderColor: "#52c41a",
        backgroundColor: "rgba(82, 196, 26, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          displayFormats: {
            day: "MMM dd",
          },
        },
        title: {
          display: true,
          text: "Date",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Count",
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
  };

  return (
    <div className="chart">
      <h3>Rewards and Vouchers Data</h3>
      <div className="chart-wrapper">
        <div className="chart-item">
          <h4>Vouchers Awarded vs Rewards Redeemed</h4>
          <div className="chart-container">
            <Line data={chartConfig} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsChart;
